/**
 * agentReducer.js — the browser's copy of the agent runtime state (CONTRACT §10.2).
 *
 * Pure: (state, msg) -> state. No clock, no DOM, no import.meta. Every inbound frame has already
 * passed validateServerMessage; frames the reducer does not know are returned unchanged.
 *
 * Idempotent: an Envelope whose seq is <= state.seq is ignored, so a replay after a reconnect or a
 * duplicated frame never applies twice. A `welcome` whose server_seq is below state.seq (a fresh
 * server database) resets the case data and seq; useAgent then reconnects for a snapshot. Ephemeral events (seq null) always apply. `snapshot`
 * replaces the case data wholesale; `replay.done` only advances seq.
 *
 * State (the contract shape, plus the extra keys marked +):
 *   status, seq, principal, specialists{id}        connection and identity
 *   + clientId, serverSeq, replaying, lastError    from welcome / replay.done / error
 *   + activeCaseId                                 set by the hook (local.active_case); wb.ui of
 *                                                  other cases does not move the drawer
 *   cases{id}, order[]                             order = newest first
 *   plans{caseId: {version, steps}}
 *   calls{caseId: {callId: ToolCall}}, callOrder{caseId: [callId]}
 *   stream{caseId: [{kind, id, at}]}               refs into speech/calls/approvals/handoffs/comments,
 *                                                  kind 'summary' refers to the case; capped at 500
 *   speech{id}, drafts{id}, approvals{id}, comments{id}, handoffs{id}
 *   draftHighlight{draftId: {kind, target, field, before, after, at, version, removed?}}
 *   files{caseId: {path: FileEntry}}, outputs{caseId: [Output]}, pages{caseId: CasePage}
 *   terminal{caseId: {execs: [{exec_id, cmd, lines:[{s,t}], exit_code, duration_ms, timed_out, partial}]}}
 *   browser{caseId: {frame, url, title, session_id, n, fallback, actions[≤50]}}
 *   memory{asset: [MemoryItem]}, memoryFlash {id, at}
 *   notifications[] newest first
 *   wb {open, tab, path, range, panel, partId, size, scroll, caseId}
 *   flashStep {caseId, stepId, at}
 *   modes {narration, working{caseId: bool}}
 *   lastUi {action, asset, caption, case_id, seq, tool_call_id}, + lastUiByCase{caseId: lastUi}
 *   + caseFocus {caseId, asset, reason, seq}, + telemetry{asset: sample}
 *
 * Local actions (dispatched by useAgent, never sent by the server): local.active_case, local.wb,
 * local.notification_read, local.last_ui, local.reset.
 */

import { ENVELOPE_TYPES } from './agentProtocol.js';

export const STREAM_CAP = 500;
export const TERMINAL_LINE_CAP = 2000;
export const BROWSER_ACTION_CAP = 50;
export const NOTIFICATION_CAP = 100;
export const DRAWER_TABS = ['files', 'terminal', 'browser', 'outputs'];

const INITIAL_WB = Object.freeze({
  open: false, tab: 'outputs', path: null, range: 'all', panel: null, partId: null,
  size: null, scroll: null, caseId: null,
});

export const initialAgentState = Object.freeze({
  status: 'idle', seq: 0, principal: null, specialists: {},
  clientId: null, serverSeq: 0, replaying: false, lastError: null, activeCaseId: null,
  cases: {}, order: [],
  plans: {}, calls: {}, callOrder: {},
  stream: {},
  speech: {}, drafts: {}, draftHighlight: {},
  approvals: {}, files: {}, outputs: {}, pages: {}, comments: {}, handoffs: {},
  terminal: {},
  browser: {},
  memory: {}, memoryFlash: null,
  notifications: [], wb: INITIAL_WB,
  flashStep: null,
  modes: { narration: 'on', working: {} }, lastUi: null, lastUiByCase: {},
  caseFocus: null, telemetry: {},
});

/* ------------------------------------------------------------- helpers */

const timeOf = (iso) => {
  const n = Date.parse(iso ?? '');
  return Number.isFinite(n) ? n : 0;
};

const byCreatedDesc = (cases) => (a, b) => timeOf(cases[b]?.created_at) - timeOf(cases[a]?.created_at);

function upsertById(list, item) {
  const arr = list || [];
  const i = arr.findIndex((x) => x.id === item.id);
  if (i === -1) return [...arr, item];
  const next = arr.slice();
  next[i] = item;
  return next;
}

function pushStream(stream, caseId, kind, id, at) {
  if (!caseId || !id) return stream;
  const list = stream[caseId] || [];
  if (list.some((e) => e.kind === kind && e.id === id)) return stream;
  const next = [...list, { kind, id, at: at ?? null }];
  return { ...stream, [caseId]: next.length > STREAM_CAP ? next.slice(next.length - STREAM_CAP) : next };
}

function addCase(state, c) {
  if (!c || !c.id) return state;
  const cases = { ...state.cases, [c.id]: c };
  const order = state.order.includes(c.id) ? state.order : [...state.order, c.id];
  return { ...state, cases, order: order.slice().sort(byCreatedDesc(cases)) };
}

function setCall(state, caseId, call) {
  if (!call || !call.id) return state;
  const cid = caseId || call.case_id;
  if (!cid) return state;
  const forCase = { ...(state.calls[cid] || {}), [call.id]: call };
  const order = state.callOrder[cid] || [];
  return {
    ...state,
    calls: { ...state.calls, [cid]: forCase },
    callOrder: order.includes(call.id) ? state.callOrder : { ...state.callOrder, [cid]: [...order, call.id] },
    stream: pushStream(state.stream, cid, 'call', call.id, call.started_at),
  };
}

/* ---- terminal ---- */

function trimTerminal(execs) {
  let total = execs.reduce((n, e) => n + e.lines.length, 0);
  if (total <= TERMINAL_LINE_CAP) return execs;
  const out = execs.map((e) => ({ ...e, lines: e.lines.slice() }));
  for (const e of out) {
    if (total <= TERMINAL_LINE_CAP) break;
    const drop = Math.min(e.lines.length, total - TERMINAL_LINE_CAP);
    e.lines.splice(0, drop);
    total -= drop;
  }
  return out;
}

/** Append a chunk; a chunk that does not end in a newline leaves its last line open for the next. */
export function appendTerminalText(exec, stream, text) {
  const clean = String(text ?? '').replace(/\r\n?/g, '\n');
  if (!clean) return exec;
  const parts = clean.split('\n');
  const endsWithNewline = clean.endsWith('\n');
  if (endsWithNewline) parts.pop();
  const lines = exec.lines.slice();
  let start = 0;
  const last = lines[lines.length - 1];
  if (exec.partial && last && last.s === stream && parts.length > 0) {
    lines[lines.length - 1] = { s: stream, t: last.t + parts[0] };
    start = 1;
  }
  for (let i = start; i < parts.length; i += 1) lines.push({ s: stream, t: parts[i] });
  return { ...exec, lines, partial: !endsWithNewline };
}

const newExec = (d) => ({
  exec_id: d.exec_id, cmd: d.cmd ?? '', tool_call_id: d.tool_call_id ?? null, lines: [],
  exit_code: null, duration_ms: null, timed_out: false, partial: false,
});

function updateExec(state, caseId, execId, fn, create) {
  if (!caseId || !execId) return state;
  const term = state.terminal[caseId] || { execs: [] };
  let execs = term.execs;
  const i = execs.findIndex((e) => e.exec_id === execId);
  if (i === -1) {
    execs = [...execs, fn(create())];
  } else {
    execs = execs.slice();
    execs[i] = fn(execs[i]);
  }
  return { ...state, terminal: { ...state.terminal, [caseId]: { execs: trimTerminal(execs) } } };
}

function execFromCall(call) {
  const r = call.result || {};
  let exec = newExec({ exec_id: r.exec_id || call.id, cmd: call.args?.cmd ?? '', tool_call_id: call.id });
  if (r.stdout_tail) exec = { ...appendTerminalText(exec, 'stdout', r.stdout_tail), partial: false };
  if (r.stderr_tail) exec = { ...appendTerminalText(exec, 'stderr', r.stderr_tail), partial: false };
  return {
    ...exec,
    exit_code: Number.isInteger(r.exit_code) ? r.exit_code : null,
    duration_ms: r.duration_ms ?? call.duration_ms ?? null,
    timed_out: Boolean(r.timed_out),
  };
}

/* ---- browser ---- */

const frameFrom = (d) => (d?.jpeg_b64 ? `data:image/jpeg;base64,${d.jpeg_b64}` : null);

function setBrowser(state, caseId, patch) {
  if (!caseId) return state;
  const prev = state.browser[caseId] || {
    frame: null, url: '', title: '', session_id: null, n: 0, fallback: false, actions: [],
  };
  return { ...state, browser: { ...state.browser, [caseId]: { ...prev, ...patch(prev) } } };
}

/* ---- workbench drawer ---- */

function applyWbUi(wb, d, caseId) {
  const next = { ...wb, caseId: caseId ?? wb.caseId };
  switch (d.op) {
    case 'drawer':
      next.open = typeof d.open === 'boolean' ? d.open : !wb.open;
      break;
    case 'tab':
      if (DRAWER_TABS.includes(d.tab)) next.tab = d.tab;
      next.open = true;
      break;
    case 'open_file':
      if (typeof d.path === 'string' && d.path) {
        next.path = d.path;
        next.tab = d.path.startsWith('outputs/') ? 'outputs' : 'files';
        next.range = typeof d.range === 'string' && d.range ? d.range : 'all';
        next.open = true;
      }
      break;
    case 'zoom':
      next.range = typeof d.range === 'string' && d.range ? d.range : 'all';
      next.open = true;
      break;
    case 'scroll':
      next.scroll = { direction: d.direction === 'up' ? 'up' : 'down', n: (wb.scroll?.n ?? 0) + 1 };
      break;
    case 'resize':
      if (['+', '-', 'max', 'min'].includes(d.size)) {
        next.size = { op: d.size, n: (wb.size?.n ?? 0) + 1 };
        next.open = d.size !== 'min';
      }
      break;
    case 'panel':
      if (d.open === false) {
        if (!d.panel || d.panel === wb.panel) { next.panel = null; next.partId = null; }
      } else if (d.panel === 'stream') {
        next.panel = null;
        next.partId = null;
      } else if (typeof d.panel === 'string') {
        next.panel = d.panel;
        next.partId = d.panel === 'dossier' ? (d.part_id ?? null) : null;
      }
      break;
    case 'read_aloud':
      if (typeof d.path === 'string' && d.path) next.path = d.path;
      break;
    default:
      return wb;
  }
  return next;
}

/* ---- snapshot ---- */

function streamFromDetail(detail) {
  const items = [];
  for (const s of detail.speech || []) items.push({ kind: 'speech', id: s.id, at: s.at });
  for (const c of detail.tool_calls || []) items.push({ kind: 'call', id: c.id, at: c.started_at });
  for (const a of detail.approvals || []) items.push({ kind: 'approval', id: a.id, at: a.requested_at });
  for (const c of detail.comments || []) items.push({ kind: 'comment', id: c.id, at: c.created_at });
  for (const h of detail.handoffs || []) items.push({ kind: 'handoff', id: h.id, at: h.created_at });
  if (detail.case?.summary) items.push({ kind: 'summary', id: detail.case.id, at: detail.case.closed_at });
  const sorted = items
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (timeOf(a.e.at) - timeOf(b.e.at)) || (a.i - b.i))
    .map(({ e }) => e);
  return sorted.length > STREAM_CAP ? sorted.slice(sorted.length - STREAM_CAP) : sorted;
}

function applySnapshot(state, msg) {
  const data = msg.data || {};
  const cases = {};
  for (const c of data.cases || []) if (c?.id) cases[c.id] = c;
  const next = {
    ...state,
    status: 'open',
    seq: msg.seq,
    replaying: false,
    cases,
    plans: {}, calls: {}, callOrder: {}, stream: {},
    speech: {}, drafts: {}, draftHighlight: {}, approvals: {},
    files: {}, outputs: {}, pages: {}, comments: {}, handoffs: {},
    terminal: {}, browser: {},
    memory: data.memory && typeof data.memory === 'object' ? { ...data.memory } : {},
    memoryFlash: null,
    notifications: (data.notifications || []).slice(0, NOTIFICATION_CAP),
    specialists: {},
    flashStep: null,
  };
  for (const s of data.specialists || []) if (s?.id) next.specialists[s.id] = s;

  for (const [cid, detail] of Object.entries(data.details || {})) {
    if (!detail) continue;
    if (detail.case?.id) cases[detail.case.id] = detail.case;
    next.plans[cid] = { version: detail.plan_version ?? 0, steps: (detail.plan || []).slice() };
    next.calls[cid] = {};
    next.callOrder[cid] = [];
    const execs = [];
    for (const call of detail.tool_calls || []) {
      next.calls[cid][call.id] = call;
      next.callOrder[cid].push(call.id);
      if (call.tool === 'wb_exec' && call.result) execs.push(execFromCall(call));
    }
    if (execs.length) next.terminal[cid] = { execs: trimTerminal(execs) };
    for (const d of detail.drafts || []) next.drafts[d.id] = d;
    for (const a of detail.approvals || []) next.approvals[a.id] = a;
    next.files[cid] = {};
    for (const f of detail.files || []) next.files[cid][f.path] = f;
    next.outputs[cid] = (detail.outputs || []).slice();
    if (detail.page) next.pages[cid] = detail.page;
    for (const c of detail.comments || []) next.comments[c.id] = c;
    for (const h of detail.handoffs || []) next.handoffs[h.id] = h;
    for (const s of detail.speech || []) next.speech[s.id] = s;
    if (detail.browser_frame) {
      const f = detail.browser_frame;
      next.browser[cid] = {
        frame: frameFrom(f), url: f.url ?? '', title: f.title ?? '', session_id: f.session_id ?? null,
        n: f.n ?? 0, fallback: false, actions: [],
      };
    }
    next.stream[cid] = streamFromDetail(detail);
  }
  next.order = Object.keys(cases).sort(byCreatedDesc(cases));
  return next;
}

/* ---- envelopes ---- */

function applyEnvelope(state, msg) {
  const d = msg.data;
  const cid = msg.case_id ?? null;
  const at = msg.ts ?? msg.seq ?? null;

  switch (msg.type) {
    case 'case.created':
    case 'case.updated': {
      let next = addCase(state, d.case);
      const c = d.case;
      if (c?.summary && (c.status === 'done' || c.closed_at)) {
        next = { ...next, stream: pushStream(next.stream, c.id, 'summary', c.id, c.closed_at ?? at) };
      }
      return next;
    }
    case 'case.focus':
      return { ...state, caseFocus: { caseId: d.case_id ?? cid, asset: d.asset ?? null, reason: d.reason ?? null, seq: msg.seq } };

    case 'plan.set': {
      const caseId = d.case_id ?? cid;
      if (!caseId) return state;
      return { ...state, plans: { ...state.plans, [caseId]: { version: d.version ?? 0, steps: (d.steps || []).slice() } } };
    }
    case 'plan.step': {
      const step = d.step;
      if (!cid || !step?.id) return state;
      const plan = state.plans[cid] || { version: 0, steps: [] };
      const steps = upsertById(plan.steps, step).slice().sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const next = { ...state, plans: { ...state.plans, [cid]: { ...plan, steps } } };
      if (d.x_flash || step.x_flash) next.flashStep = { caseId: cid, stepId: step.id, at };
      return next;
    }

    case 'tool.call':
    case 'tool.result':
      return setCall(state, cid, d.call);

    case 'speech': {
      if (!d.id) return state;
      const caseId = d.case_id ?? cid;
      return {
        ...state,
        speech: { ...state.speech, [d.id]: d },
        stream: pushStream(state.stream, caseId, 'speech', d.id, d.at ?? at),
      };
    }

    case 'ui.action': {
      const lastUi = {
        action: d.action, asset: d.asset ?? null, caption: d.caption ?? null,
        case_id: cid, seq: msg.seq, tool_call_id: d.tool_call_id ?? null,
      };
      return {
        ...state,
        lastUi,
        lastUiByCase: cid ? { ...state.lastUiByCase, [cid]: lastUi } : state.lastUiByCase,
      };
    }

    case 'wb.ui': {
      if (cid && state.activeCaseId && cid !== state.activeCaseId) return state;
      const wb = applyWbUi(state.wb, d, cid);
      return wb === state.wb ? state : { ...state, wb };
    }

    case 'wb.exec.start':
      return updateExec(state, cid, d.exec_id, (e) => ({
        ...e, cmd: d.cmd ?? e.cmd, tool_call_id: d.tool_call_id ?? e.tool_call_id,
      }), () => newExec(d));
    case 'wb.term.chunk':
      return updateExec(state, cid, d.exec_id,
        (e) => appendTerminalText(e, d.stream === 'stderr' ? 'stderr' : 'stdout', d.text),
        () => newExec(d));
    case 'wb.exec.end':
      return updateExec(state, cid, d.exec_id, (e) => {
        const filled = e.lines.length === 0 && d.tail ? appendTerminalText(e, 'stdout', d.tail) : e;
        return {
          ...filled,
          partial: false,
          exit_code: Number.isInteger(d.exit_code) ? d.exit_code : null,
          duration_ms: d.duration_ms ?? null,
          timed_out: Boolean(d.timed_out),
        };
      }, () => newExec(d));

    case 'wb.browser.frame':
      return setBrowser(state, cid, () => ({
        frame: frameFrom(d), url: d.url ?? '', title: d.title ?? '', session_id: d.session_id ?? null, n: d.n ?? 0,
      }));
    case 'wb.browser.action':
      return setBrowser(state, cid, (prev) => {
        const actions = [...prev.actions, {
          kind: d.kind, selector: d.selector ?? null, value_preview: d.value_preview ?? null,
          url: d.url ?? '', tool_call_id: d.tool_call_id ?? null, at,
        }];
        return {
          actions: actions.length > BROWSER_ACTION_CAP ? actions.slice(actions.length - BROWSER_ACTION_CAP) : actions,
          url: d.url || prev.url,
          session_id: d.session_id ?? prev.session_id,
          fallback: prev.fallback || d.kind === 'fallback',
        };
      });

    case 'file.added':
    case 'file.updated': {
      const f = d.file;
      if (!cid || !f?.path) return state;
      return { ...state, files: { ...state.files, [cid]: { ...(state.files[cid] || {}), [f.path]: f } } };
    }
    case 'output.published': {
      const o = d.output;
      const caseId = o?.case_id ?? cid;
      if (!caseId || !o?.id) return state;
      return { ...state, outputs: { ...state.outputs, [caseId]: upsertById(state.outputs[caseId], o) } };
    }

    case 'draft.created':
    case 'draft.updated': {
      const draft = d.draft;
      if (!draft?.id) return state;
      return { ...state, drafts: { ...state.drafts, [draft.id]: draft } };
    }
    case 'draft.op': {
      const draft = d.draft;
      if (!draft?.id) return state;
      const next = { ...state, drafts: { ...state.drafts, [draft.id]: draft } };
      const hl = d.result?.highlight;
      if (hl && hl.kind) {
        const entry = {
          kind: hl.kind, target: hl.target ?? null, field: hl.field ?? null,
          before: hl.before ?? null, after: hl.after ?? null,
          at, version: d.result.version ?? draft.version,
        };
        if (hl.kind === 'removed_line') {
          entry.removed = (state.drafts[draft.id]?.lines || []).find((l) => l.id === hl.target) ?? null;
        }
        next.draftHighlight = { ...state.draftHighlight, [draft.id]: entry };
      }
      return next;
    }

    case 'approval.pending':
    case 'approval.updated': {
      const a = d.approval;
      if (!a?.id) return state;
      const approvals = { ...state.approvals, [a.id]: a };
      const caseId = a.case_id ?? cid;
      const next = {
        ...state,
        approvals,
        stream: pushStream(state.stream, caseId, 'approval', a.id, a.requested_at ?? at),
      };
      // A decided approval (by voice or on the card) resolves the case's "Approval needed" toast.
      const read = msg.type === 'approval.updated' ? resolvedApprovalNotifications(next, caseId) : [];
      if (read.length === 0) return next;
      const ids = new Set(read);
      return { ...next, notifications: state.notifications.map((n) => (ids.has(n.id) ? { ...n, read: true } : n)) };
    }

    case 'page.updated': {
      const page = d.page;
      const caseId = page?.case_id ?? cid;
      if (!caseId || !page) return state;
      const prev = state.pages[caseId];
      if (prev && (prev.version ?? 0) > (page.version ?? 0)) return state;
      return { ...state, pages: { ...state.pages, [caseId]: page } };
    }
    case 'comment.added': {
      const c = d.comment;
      if (!c?.id) return state;
      return {
        ...state,
        comments: { ...state.comments, [c.id]: c },
        stream: pushStream(state.stream, c.case_id ?? cid, 'comment', c.id, c.created_at ?? at),
      };
    }
    case 'handoff': {
      const h = d.handoff;
      if (!h?.id) return state;
      return {
        ...state,
        handoffs: { ...state.handoffs, [h.id]: h },
        stream: pushStream(state.stream, h.case_id ?? cid, 'handoff', h.id, h.created_at ?? at),
      };
    }

    case 'memory.added':
    case 'memory.updated':
    case 'memory.deleted': {
      const item = d.item;
      if (!item?.id) return state;
      const asset = item.asset || '_';
      // An edit may move nothing between assets, but drop the id everywhere to be safe.
      const memory = {};
      for (const [k, list] of Object.entries(state.memory)) memory[k] = (list || []).filter((m) => m.id !== item.id);
      const gone = msg.type === 'memory.deleted' || item.deleted;
      if (!gone) memory[asset] = upsertById(memory[asset], item);
      return { ...state, memory, memoryFlash: gone ? state.memoryFlash : { id: item.id, at } };
    }

    case 'notification': {
      const n = d.notification;
      if (!n?.id) return state;
      const rest = state.notifications.filter((x) => x.id !== n.id);
      return { ...state, notifications: [n, ...rest].slice(0, NOTIFICATION_CAP) };
    }

    case 'agent.mode': {
      const modes = { ...state.modes, working: { ...(state.modes.working || {}) } };
      if ((d.client_id === null || d.client_id === undefined || d.client_id === state.clientId)
        && (d.narration === 'on' || d.narration === 'off')) {
        modes.narration = d.narration;
      }
      if (cid && typeof d.working === 'boolean') modes.working[cid] = d.working;
      return { ...state, modes };
    }

    case 'telemetry.sample':
      if (!d.asset) return state;
      return { ...state, telemetry: { ...state.telemetry, [d.asset]: d } };

    default:
      return state;
  }
}

/**
 * Ids of the unread approval_needed notifications of `caseId` once no approval of that case is
 * pending any more (approved, rejected, expired, submitted or failed). useAgent marks the same ids
 * read on the server, so a reload does not bring them back.
 */
export function resolvedApprovalNotifications(state, caseId) {
  if (!caseId) return [];
  const pending = Object.values(state.approvals || {}).some((a) => a?.case_id === caseId && a.status === 'pending');
  if (pending) return [];
  return (state.notifications || [])
    .filter((n) => n.kind === 'approval_needed' && n.case_id === caseId && !n.read)
    .map((n) => n.id);
}

/**
 * The notifications to show as toasts: an approval_needed one only while its case still waits on
 * an approval (a case the client does not hold yet keeps it, so a new alert is never lost).
 */
export function visibleNotifications(state) {
  const cases = state?.cases || {};
  return (state?.notifications || []).filter((n) => {
    if (n.kind !== 'approval_needed') return true;
    const c = n.case_id ? cases[n.case_id] : null;
    return !c || c.waiting_reason === 'approval';
  });
}

/* ---- local actions ---- */

function applyLocal(state, msg) {
  switch (msg.type) {
    case 'local.active_case': {
      const caseId = msg.caseId ?? null;
      if (caseId === state.activeCaseId) return state;
      // The Workbench viewer shows files of the active case: another case's open file, draft path
      // or part dossier would resolve against the new case id (404 / "not loaded").
      const wb = {
        ...state.wb,
        path: null,
        range: 'all',
        panel: state.wb.panel === 'dossier' ? null : state.wb.panel,
        partId: null,
        caseId,
      };
      return { ...state, activeCaseId: caseId, wb };
    }
    case 'local.wb': {
      const patch = msg.patch || {};
      const wb = { ...state.wb };
      for (const key of ['open', 'tab', 'path', 'range', 'panel', 'partId']) {
        if (key in patch) wb[key] = patch[key];
      }
      if (wb.panel !== 'dossier') wb.partId = null;
      if (!DRAWER_TABS.includes(wb.tab)) wb.tab = state.wb.tab;
      return { ...state, wb };
    }
    case 'local.notification_read':
      return {
        ...state,
        notifications: state.notifications.map((n) => (n.id === msg.id ? { ...n, read: true } : n)),
      };
    case 'local.last_ui': {
      const lastUi = msg.lastUi;
      if (!lastUi) return state;
      return {
        ...state,
        lastUi,
        lastUiByCase: lastUi.case_id ? { ...state.lastUiByCase, [lastUi.case_id]: lastUi } : state.lastUiByCase,
      };
    }
    case 'local.reset':
      return { ...initialAgentState, activeCaseId: state.activeCaseId };
    default:
      return state;
  }
}

/* ------------------------------------------------------------- reducer */

export function agentReducer(state = initialAgentState, msg) {
  if (!msg || typeof msg.type !== 'string') return state;
  if (msg.type.startsWith('local.')) return applyLocal(state, msg);

  switch (msg.type) {
    case 'welcome':
      // The server's seq went backwards (a fresh database behind the same URL): everything held
      // here belongs to the old one, and every new event would be dropped as already seen.
      if (Number.isInteger(msg.server_seq) && msg.server_seq < state.seq) {
        return {
          ...initialAgentState,
          activeCaseId: state.activeCaseId,
          wb: state.wb,
          status: 'open',
          principal: msg.principal ?? state.principal,
          clientId: msg.client_id ?? state.clientId,
          serverSeq: msg.server_seq,
          replaying: msg.mode === 'replay',
        };
      }
      return {
        ...state,
        status: 'open',
        principal: msg.principal ?? state.principal,
        clientId: msg.client_id ?? state.clientId,
        serverSeq: Number.isInteger(msg.server_seq) ? msg.server_seq : state.serverSeq,
        replaying: msg.mode === 'replay',
      };
    case 'snapshot':
      return applySnapshot(state, msg);
    case 'replay.done':
      return { ...state, replaying: false, seq: Math.max(state.seq, msg.seq ?? 0) };
    case 'pong':
      return state;
    case 'error':
      return {
        ...state,
        lastError: { code: msg.code, message: msg.message ?? '', ref: msg.ref ?? null, n: (state.lastError?.n ?? 0) + 1 },
      };
    default:
      break;
  }

  if (!ENVELOPE_TYPES.has(msg.type) || !msg.data || typeof msg.data !== 'object') return state;
  const isInt = Number.isInteger(msg.seq);
  if (isInt && msg.seq <= state.seq) return state;
  const next = applyEnvelope(state, msg);
  if (!isInt) return next;
  return next === state ? { ...state, seq: msg.seq } : { ...next, seq: msg.seq };
}

/* ------------------------------------------------------------- selectors */

/** Drafts of a case, oldest first, superseded ones left out. */
export function selectCaseDrafts(state, caseId) {
  return Object.values(state.drafts)
    .filter((d) => d.case_id === caseId && d.status !== 'superseded')
    .sort((a, b) => timeOf(a.created_at) - timeOf(b.created_at));
}

export function selectCaseApprovals(state, caseId) {
  return Object.values(state.approvals)
    .filter((a) => a.case_id === caseId)
    .sort((a, b) => timeOf(a.requested_at) - timeOf(b.requested_at));
}

export function selectPendingApproval(state, caseId) {
  const pending = selectCaseApprovals(state, caseId).filter((a) => a.status === 'pending');
  return pending[pending.length - 1] ?? null;
}

export function selectCaseComments(state, caseId) {
  return Object.values(state.comments)
    .filter((c) => c.case_id === caseId)
    .sort((a, b) => timeOf(a.created_at) - timeOf(b.created_at));
}

/** The stream with each ref resolved to its object; refs whose object is missing are skipped. */
export function selectStream(state, caseId) {
  const out = [];
  for (const ref of state.stream[caseId] || []) {
    let item = null;
    if (ref.kind === 'speech') item = state.speech[ref.id];
    else if (ref.kind === 'call') item = state.calls[caseId]?.[ref.id];
    else if (ref.kind === 'approval') item = state.approvals[ref.id];
    else if (ref.kind === 'comment') item = state.comments[ref.id];
    else if (ref.kind === 'handoff') item = state.handoffs[ref.id];
    else if (ref.kind === 'summary') item = state.cases[ref.id];
    if (item) out.push({ ...ref, item });
  }
  return out;
}

/** Everything the agent components need for one case, from one state. */
export function selectCaseView(state, caseId) {
  const caseObj = caseId ? state.cases[caseId] ?? null : null;
  const files = Object.values(state.files[caseId] || {}).sort((a, b) => a.path.localeCompare(b.path));
  return {
    caseObj,
    plan: state.plans[caseId] ?? { version: 0, steps: [] },
    calls: state.calls[caseId] ?? {},
    callOrder: state.callOrder[caseId] ?? [],
    stream: selectStream(state, caseId),
    drafts: selectCaseDrafts(state, caseId),
    approvals: selectCaseApprovals(state, caseId),
    pendingApproval: selectPendingApproval(state, caseId),
    files,
    outputs: state.outputs[caseId] ?? [],
    page: state.pages[caseId] ?? null,
    comments: selectCaseComments(state, caseId),
    terminal: state.terminal[caseId]?.execs ?? [],
    browser: state.browser[caseId] ?? null,
    flashStep: state.flashStep?.caseId === caseId ? state.flashStep : null,
    memory: caseObj ? state.memory[caseObj.asset] ?? [] : [],
  };
}

/** True when the principal holds one of the approval's required roles (specialists never do). */
export function canApprove(principal, approval) {
  if (!principal || !approval || principal.kind === 'specialist') return false;
  const roles = principal.roles || [];
  return (approval.required_roles || []).some((r) => roles.includes(r));
}
